import React, { useMemo, useState } from "react";
import { Collapse, Table, Input, Select, Switch, Button, Typography, Tooltip, Modal, message } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  SwapOutlined,
  TableOutlined,
  EyeOutlined,
  CopyOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnsType } from "antd/es/table";
import type { CollapseProps } from "antd";
import MainLayout from "@/components/layout/MainLayout";
import { useUser } from "@/contexts/UserContext";
import { useTender } from "@/contexts/TenderContext";
import { rateEngineApi, RATE_ENGINE_QUERY_KEYS } from "@/features/rate-engine/api";
import { useSiteOppNo } from "@/features/rate-engine/hooks";
import { buildSectionConfig, type FieldType, type JobType, type TableConfigRow } from "./types";
import { SECTION_CATEGORY_CONFIG } from "./sectionCategoryConfig";
import { DEFAULT_EXCEL_SHEET_NAMES } from "./defaultExcelSheetNames";
import TablePreviewModal from "./TablePreviewModal";
import type { DynamicColumnUpdate } from "./DynamicColumnsConfigModal";

const { Title, Text } = Typography;

// ─── Region resolution (mirrors Rate Engine's region fallback logic) ──────────
const REGIONS = [
  { label: "North West", value: "north-west" },
  { label: "West Midlands", value: "west-midlands" },
  { label: "East Midlands", value: "east-midlands" },
  { label: "Eastern Counties", value: "eastern-counties" },
  { label: "Southern", value: "southern" },
  { label: "South West", value: "south-west" },
  { label: "Yorkshire", value: "yorkshire" },
  { label: "Yorkshire (Morrisons)", value: "YKSM" },
];

function toRegionSlug(label: string | undefined): string {
  if (!label || label === "All Regions") return "";
  const found = REGIONS.find((r) => r.label.toLowerCase() === label.toLowerCase());
  return found?.value ?? label.toLowerCase().replace(/\s+/g, "-");
}

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyRow = (): TableConfigRow => ({
  id: nextRowId(),
  tableColumn: "",
  evaluateCode: "",
  variant: "",
  category: "",
  subCategory: "",
  fieldType: "Text",
  isDynamic: false,
  unitConversion: false,
  unitConversionValue: 0,
  options: "",
  takeoffImportName: "",
});

const FIELD_TYPE_OPTIONS: { label: string; value: FieldType }[] = [
  { label: "Number", value: "Number" },
  { label: "Text", value: "Text" },
  { label: "Dropdown", value: "Dropdown" },
];

interface SectionsConfiguratorProps {
  jobType: JobType;
  jobTypeLabel: string;
  sections: string[];
  onChangeJobType: () => void;
}

const SectionsConfigurator: React.FC<SectionsConfiguratorProps> = ({
  jobType,
  jobTypeLabel,
  sections,
  onChangeJobType,
}) => {
  const navigate = useNavigate();
  const user = useUser();
  const { state: tenderState } = useTender();

  const effectiveRegion = useMemo(() => {
    const fromProject = tenderState.projectName ? toRegionSlug(tenderState.region) : "";
    const fromUser = toRegionSlug(user?.region ?? "");
    return fromProject || fromUser || "north-west";
  }, [tenderState.region, tenderState.projectName, user?.region]);

  const siteName = tenderState.projectName || undefined;

  // Verified opportunity number — undefined for legacy untagged sites (see `useSiteOppNo`).
  const { oppNo, isReady: isSiteScopeReady } = useSiteOppNo(effectiveRegion, siteName, tenderState.opportunityNo || undefined);

  const { data: templatesDict, isLoading: templatesLoading } = useQuery({
    queryKey: RATE_ENGINE_QUERY_KEYS.activityTemplates(effectiveRegion, siteName, oppNo),
    queryFn: () => rateEngineApi.getActivityTemplates(effectiveRegion, siteName, oppNo),
    enabled: isSiteScopeReady,
    staleTime: 300_000,
    retry: 1,
  });

  const evaluateCodeOptions = useMemo(() => {
    if (!templatesDict) return [];
    return Object.values(templatesDict)
      .map((template) => ({ value: template.c, label: `${template.c} — ${template.n}` }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [templatesDict]);

  const [sectionRows, setSectionRows] = useState<Record<string, TableConfigRow[]>>(() =>
    Object.fromEntries(sections.map((section) => [section, []])),
  );

  const [sectionExcelSheetName, setSectionExcelSheetName] = useState<Record<string, string>>(() =>
    Object.fromEntries(sections.map((section) => [section, DEFAULT_EXCEL_SHEET_NAMES[section] ?? ""])),
  );

  const [previewSection, setPreviewSection] = useState<string | null>(null);
  const [previewTableSection, setPreviewTableSection] = useState<string | null>(null);
  const [configuringRow, setConfiguringRow] = useState<{ section: string; rowId: string } | null>(null);
  const [configuringOptionsRow, setConfiguringOptionsRow] = useState<{ section: string; rowId: string } | null>(
    null,
  );

  const getSectionConfig = (section: string) =>
    buildSectionConfig(jobType, section, sectionRows[section] ?? [], sectionExcelSheetName[section] ?? "");

  const previewJson = useMemo(
    () => (previewSection ? JSON.stringify(getSectionConfig(previewSection), null, 2) : ""),
    [previewSection, sectionRows, sectionExcelSheetName],
  );

  const previewTableConfig = useMemo(
    () => (previewTableSection ? getSectionConfig(previewTableSection) : null),
    [previewTableSection, sectionRows, sectionExcelSheetName],
  );

  const configuringRowData = configuringRow
    ? (sectionRows[configuringRow.section] ?? []).find((row) => row.id === configuringRow.rowId) ?? null
    : null;

  const configuringOptionsRowData = configuringOptionsRow
    ? (sectionRows[configuringOptionsRow.section] ?? []).find((row) => row.id === configuringOptionsRow.rowId) ?? null
    : null;

  const handleCopyPreview = async () => {
    try {
      await navigator.clipboard.writeText(previewJson);
      message.success("JSON copied to clipboard");
    } catch {
      message.error("Failed to copy JSON");
    }
  };

  const addRow = (section: string) => {
    setSectionRows((prev) => ({
      ...prev,
      [section]: [...(prev[section] ?? []), createEmptyRow()],
    }));
  };

  const removeRow = (section: string, rowId: string) => {
    setSectionRows((prev) => ({
      ...prev,
      [section]: (prev[section] ?? []).filter((row) => row.id !== rowId),
    }));
  };

  const updateRow = <K extends keyof TableConfigRow>(
    section: string,
    rowId: string,
    field: K,
    value: TableConfigRow[K],
  ) => {
    setSectionRows((prev) => ({
      ...prev,
      [section]: (prev[section] ?? []).map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleSaveDynamicOptions = (updates: DynamicColumnUpdate[]) => {
    if (!previewTableSection) return;
    const section = previewTableSection;
    updates.forEach(({ rowId, options, unitConversionValue }) => {
      updateRow(section, rowId, "options", options.join(", "));
      if (unitConversionValue !== undefined) {
        updateRow(section, rowId, "unitConversionValue", unitConversionValue);
      }
    });
  };

  const buildColumns = (section: string): ColumnsType<TableConfigRow> => {
    const categoryConfig = SECTION_CATEGORY_CONFIG[section];
    const variantColumn = categoryConfig?.variantColumn;
    const categoryOptionsFor = (record: TableConfigRow) =>
      variantColumn ? variantColumn.categoriesByOption[record.variant] ?? [] : categoryConfig?.categories ?? [];

    const columns: ColumnsType<TableConfigRow> = [];

    if (variantColumn) {
      columns.push({
        title: variantColumn.label,
        dataIndex: "variant",
        key: "variant",
        width: 220,
        render: (_: string, record) => (
          <Select
            value={record.variant || undefined}
            placeholder={`Select ${variantColumn.label.toLowerCase()}`}
            allowClear
            options={variantColumn.options}
            style={{ width: "100%" }}
            onChange={(value) => {
              updateRow(section, record.id, "variant", value ?? "");
              updateRow(section, record.id, "category", "");
              updateRow(section, record.id, "subCategory", "");
            }}
          />
        ),
      });
    }

    if (!categoryConfig?.hideCategoryColumns) {
      columns.push(
        {
          title: "Category",
          dataIndex: "category",
          key: "category",
          width: 160,
          render: (_: string, record) =>
            categoryConfig ? (
              <Select
                value={record.category || undefined}
                placeholder="Select category"
                allowClear
                options={categoryOptionsFor(record)}
                style={{ width: "100%" }}
                onChange={(value) => {
                  updateRow(section, record.id, "category", value ?? "");
                  updateRow(section, record.id, "subCategory", "");
                }}
              />
            ) : (
              <Input
                value={record.category}
                placeholder="Category"
                onChange={(e) => updateRow(section, record.id, "category", e.target.value)}
              />
            ),
        },
        {
          title: "Sub-category",
          dataIndex: "subCategory",
          key: "subCategory",
          width: 160,
          render: (_: string, record) =>
            categoryConfig ? (
              <Select
                value={record.subCategory || undefined}
                placeholder="Select sub-category"
                allowClear
                options={categoryConfig.subCategoriesByCategory[record.category] ?? []}
                style={{ width: "100%" }}
                onChange={(value) => updateRow(section, record.id, "subCategory", value ?? "")}
              />
            ) : (
              <Input
                value={record.subCategory}
                placeholder="Sub-category"
                onChange={(e) => updateRow(section, record.id, "subCategory", e.target.value)}
              />
            ),
        },
      );
    }

    columns.push(
      {
        title: "Table Columns",
        dataIndex: "tableColumn",
        key: "tableColumn",
        width: 200,
        render: (_: string, record) => (
          <Input
            value={record.tableColumn}
            placeholder="Column name"
            onChange={(e) => updateRow(section, record.id, "tableColumn", e.target.value)}
          />
        ),
      },
      {
        title: "Evaluate Code",
        dataIndex: "evaluateCode",
        key: "evaluateCode",
        width: 260,
        render: (_: string, record) => (
          <Select
            value={record.evaluateCode || undefined}
            placeholder="Select evaluate code"
            showSearch
            allowClear
            loading={templatesLoading}
            options={evaluateCodeOptions}
            optionFilterProp="label"
            style={{ width: "100%" }}
            onChange={(value) => updateRow(section, record.id, "evaluateCode", value ?? "")}
          />
        ),
      },
      {
        title: "Field Type",
        dataIndex: "fieldType",
        key: "fieldType",
        width: 180,
        render: (_: FieldType, record) => (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Select<FieldType>
              value={record.fieldType}
              options={FIELD_TYPE_OPTIONS}
              style={{ width: "100%" }}
              onChange={(value) => {
                updateRow(section, record.id, "fieldType", value);
                // Options are also used by Dynamic Header, so only clear
                // them when this row isn't relying on them for that too.
                if (record.fieldType === "Dropdown" && value !== "Dropdown" && !record.isDynamic) {
                  updateRow(section, record.id, "options", "");
                }
              }}
            />
            {record.fieldType === "Dropdown" && (
              <Tooltip title="Configure Options">
                <Button
                  type="text"
                  size="small"
                  icon={<UnorderedListOutlined />}
                  onClick={() => setConfiguringOptionsRow({ section, rowId: record.id })}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Dynamic Header",
        dataIndex: "isDynamic",
        key: "isDynamic",
        width: 130,
        align: "center" as const,
        render: (_: boolean, record) => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <Switch
              checked={record.isDynamic}
              onChange={(checked) => {
                updateRow(section, record.id, "isDynamic", checked);
                // Unit Conversion is only reachable via this row's Configure
                // Headers gear (gated on isDynamic) — turning Dynamic Header
                // off would otherwise leave it stuck on with no way to
                // switch it off again.
                if (!checked && record.unitConversion) {
                  updateRow(section, record.id, "unitConversion", false);
                  updateRow(section, record.id, "unitConversionValue", 0);
                }
              }}
            />
            {record.isDynamic && (
              <Tooltip title="Configure">
                <Button
                  type="text"
                  size="small"
                  icon={<SettingOutlined />}
                  onClick={() => setConfiguringRow({ section, rowId: record.id })}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Takeoff Import Name",
        dataIndex: "takeoffImportName",
        key: "takeoffImportName",
        width: 200,
        render: (_: string, record) => (
          <Input
            value={record.takeoffImportName}
            placeholder="e.g. A2"
            onChange={(e) => updateRow(section, record.id, "takeoffImportName", e.target.value)}
          />
        ),
      },
      {
        title: "",
        key: "actions",
        width: 50,
        align: "center" as const,
        render: (_: unknown, record) => (
          <Tooltip title="Remove row">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeRow(section, record.id)}
            />
          </Tooltip>
        ),
      },
    );

    return columns;
  };

  const collapseItems: CollapseProps["items"] = sections.map((section) => {
    const rows = sectionRows[section] ?? [];
    return {
      key: section,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", width: "100%" }}>
          <Text strong>{section}</Text>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "1px 8px",
              borderRadius: 10,
              backgroundColor: "#f0f5ff",
              border: "1px solid #adc6ff",
              color: "#1d39c4",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {rows.length} field{rows.length === 1 ? "" : "s"}
          </span>
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Text style={{ fontSize: 12, color: "#6b7280", whiteSpace: "nowrap" }}>
              Import Excel Sheet Name
            </Text>
            <Input
              value={sectionExcelSheetName[section] ?? ""}
              placeholder="Excel sheet name for this section"
              size="small"
              style={{ width: 220 }}
              onChange={(e) =>
                setSectionExcelSheetName((prev) => ({ ...prev, [section]: e.target.value }))
              }
            />
            <Tooltip title="Preview JSON configuration">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => setPreviewSection(section)}
              >
                JSON
              </Button>
            </Tooltip>
            <Tooltip title="Preview dynamic table">
              <Button
                icon={<TableOutlined />}
                size="small"
                onClick={() => setPreviewTableSection(section)}
              >
                Preview
              </Button>
            </Tooltip>
          </div>
        </div>
      ),
      children: (
        <div>
          <Table<TableConfigRow>
            className="table-config-table"
            dataSource={rows}
            columns={buildColumns(section)}
            rowKey="id"
            bordered
            size="middle"
            pagination={false}
            locale={{ emptyText: 'No fields configured yet — click "Add Row" to get started.' }}
          />
          <div style={{ marginTop: 12 }}>
            <Button icon={<PlusOutlined />} onClick={() => addRow(section)}>
              Add Row
            </Button>
          </div>
        </div>
      ),
    };
  });

  return (
    <MainLayout canProceed={false} showFooter={false}>
      <div style={{ padding: "24px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <TableOutlined style={{ fontSize: 20, color: "#0891b2" }} />
          <Title level={4} style={{ margin: 0 }}>
            Takeoff Configuration — {jobTypeLabel}
          </Title>
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <Button icon={<SwapOutlined />} onClick={onChangeJobType}>
              Change Job Type
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/")}>
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Info strip */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 6,
            backgroundColor: "#dbeafe",
            borderLeft: "4px solid #3b82f6",
          }}
        >
          <div>
            <Text strong style={{ fontSize: 13, color: "#1e40af" }}>
              Takeoff Table Field Configuration
            </Text>
            <Text style={{ display: "block", fontSize: 12, color: "#3730a3", marginTop: 1 }}>
              Define the columns shown on each workflow's takeoff table, and how they map onto Excel import names and evaluate codes.
            </Text>
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 8,
            border: "1px solid rgba(0,0,0,0.08)",
            padding: "16px",
          }}
        >
          <Collapse accordion items={collapseItems} />
        </div>

        <Modal
          title={previewSection ? `${previewSection} — Takeoff Configuration JSON` : ""}
          open={previewSection !== null}
          onCancel={() => setPreviewSection(null)}
          footer={[
            <Button key="copy" icon={<CopyOutlined />} onClick={handleCopyPreview}>
              Copy
            </Button>,
            <Button key="close" type="primary" onClick={() => setPreviewSection(null)}>
              Close
            </Button>,
          ]}
          width={720}
        >
          <pre
            style={{
              backgroundColor: "#0f172a",
              color: "#e2e8f0",
              padding: 16,
              borderRadius: 6,
              maxHeight: "60vh",
              overflow: "auto",
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {previewJson}
          </pre>
        </Modal>

        <TablePreviewModal
          open={previewTableSection !== null}
          config={previewTableConfig}
          onClose={() => setPreviewTableSection(null)}
          onSaveDynamicOptions={handleSaveDynamicOptions}
        />

        <Modal
          title="Configure Dynamic Header"
          open={configuringRow !== null}
          onCancel={() => setConfiguringRow(null)}
          footer={[
            <Button key="close" type="primary" onClick={() => setConfiguringRow(null)}>
              Close
            </Button>,
          ]}
          width={360}
        >
          {configuringRowData && configuringRow && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Text>Unit Conversion</Text>
                <Switch
                  checked={configuringRowData.unitConversion}
                  onChange={(checked) => {
                    updateRow(configuringRow.section, configuringRow.rowId, "unitConversion", checked);
                    if (!checked) {
                      updateRow(configuringRow.section, configuringRow.rowId, "unitConversionValue", 0);
                    }
                  }}
                />
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title="Configure Options"
          open={configuringOptionsRow !== null}
          onCancel={() => setConfiguringOptionsRow(null)}
          footer={[
            <Button key="close" type="primary" onClick={() => setConfiguringOptionsRow(null)}>
              Close
            </Button>,
          ]}
          width={400}
        >
          {configuringOptionsRowData && configuringOptionsRow && (
            <Input
              value={configuringOptionsRowData.options}
              placeholder="Comma-separated options"
              onChange={(e) =>
                updateRow(configuringOptionsRow.section, configuringOptionsRow.rowId, "options", e.target.value)
              }
            />
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default SectionsConfigurator;
